import React, { useState, useEffect } from "react";
import {
  TextField,
  RadioGroup,
  FormControlLabel,
  Radio,
  Typography,
  Card,
  CardContent,
  Button,
  Box,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
} from "@mui/material";
import MotorcycleIcon from "@mui/icons-material/TwoWheeler";
import CarIcon from "@mui/icons-material/DirectionsCar";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import DirectionsBusIcon from "@mui/icons-material/DirectionsBus";
import LocalTaxiIcon from "@mui/icons-material/LocalTaxi";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";

// Premium Data
const premiumData = {
  privateCar: [
    { label: "Up to 1300 cc", value: 1300, basic: 750, driver: 150 },
    { label: "Up to 1800 cc", value: 1800, basic: 1250, driver: 150 },
    { label: "Up to 3000 cc", value: 3000, basic: 1750, driver: 150 },
    { label: "Over 3000 cc", value: Infinity, basic: 2250, driver: 150 },
  ],
  motorcycle: [
    { label: "Up to 150 cc", value: 150, basic: 500, passenger: 225, driver: 250 },
    { label: "Up to 250 cc", value: 250, basic: 650, passenger: 225, driver: 250 },
    { label: "Up to 350 cc", value: 350, basic: 800, passenger: 225, driver: 250 },
    { label: "Over 350 cc", value: Infinity, basic: null, passenger: null, driver: null },
  ],
  threeWheeler: [
    { label: "Up to 150 cc", value: 150, basic: 500, passenger: 225, driver: 250 },
    { label: "Up to 250 cc", value: 250, basic: 650, passenger: 225, driver: 250 },
    { label: "Up to 350 cc", value: 350, basic: 800, passenger: 225, driver: 250 },
    { label: "Over 350 cc", value: Infinity, basic: null, passenger: null, driver: null },
  ],
  truck: [
    { label: "Below 5 Ton", value: 4.99, basic: 3500 * 0.8 },
    { label: "5 Ton", value: 5, basic: 3500 },
    { label: "Over 5 Ton", value: 6, basic: 3500 },
  ],
  bus: [
    { label: "Single Deck: Up to 18", value: 18, basic: 2500 },
    { label: "Single Deck: 19–24", value: 24, basic: 2625 },
    { label: "Single Deck: 25–30", value: 30, basic: 2750 },
    { label: "Single Deck: 31–36", value: 36, basic: 3000 },
    { label: "Single Deck: Over 36", value: 37, basic: 3250 },
    { label: "Double Deck: Up to 60", value: 60, basic: 3250 },
    { label: "Double Deck: Over 60", value: 61, basic: 3750 },
  ],
  taxi: { basic: 1670, driver: 150 },
};

const vehicleOptions = [
  { value: "motorcycle", label: "Motorcycle", icon: <MotorcycleIcon /> },
  { value: "privateCar", label: "Private Car", icon: <CarIcon /> },
  { value: "truck", label: "Truck", icon: <LocalShippingIcon /> },
  { value: "bus", label: "Bus", icon: <DirectionsBusIcon /> },
  { value: "taxi", label: "Taxi / Public Hire", icon: <LocalTaxiIcon /> },
];

const TpForm = ({ onSubmit }) => {
  const getTomorrow = () => {
    const today = new Date();
    today.setDate(today.getDate() + 1);
    return today.toISOString().split("T")[0];
  };

  const [formData, setFormData] = useState({
    vehicleType: "motorcycle",
    engineCC: "",
    loadTon: "",
    extraTons: "",
    seats: "",
    passengers: "",
    vehicleNumber: "",
    ownerName: "",
    engineNumber: "",
    chassisNumber: "",
    mfgYear: "",
    policyStartDate: getTomorrow(),
    registrationDoc: null,
    price: 0,
    premiumBreakdown: {},
    isScanned: false
  });

  const [loading, setLoading] = useState(false);
  const [isManualPremium, setIsManualPremium] = useState(false);
  const [openDetails, setOpenDetails] = useState(false);

  const handleRegistrationDoc = async (file) => {
    setLoading(true);
    const uploadData = new FormData();
    uploadData.append("registrationDoc", file);
    try {
      const response = await fetch("https://www.bgicdigital.com/admin/scan_registrationdoc.php", {
        method: "POST",
        body: uploadData,
      });
      const result = await response.json();
      if (result.success) {
        setFormData(prev => ({
          ...prev,
          vehicleNumber: result.data.reg_no || "",
          engineNumber: result.data.engine_no || "",
          chassisNumber: result.data.chassis_no || "",
          mfgYear: result.data.mfg_year || "",
          isScanned: true
        }));
      }
    } catch (e) { console.error(e); } finally { setLoading(false); }
  };

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    if (files) {
      setFormData(prev => ({ ...prev, [name]: files[0] }));
      handleRegistrationDoc(files[0]);
      return;
    }
    let val = value;
    if (["engineCC", "loadTon", "extraTons", "seats", "passengers"].includes(name)) {
      val = val === "Infinity" ? Infinity : Number(val);
    }
    setFormData((prev) => ({ ...prev, [name]: val }));
  };

  const calculatePremium = (data) => {
    let basic = 0, passenger = 0, driver = 0;
    const { vehicleType, engineCC, loadTon, seats, passengers, extraTons } = data;

    if (vehicleType === "privateCar") {
      const rate = premiumData.privateCar.find((r) => engineCC <= r.value);
      if (!rate) return null;
      basic = rate.basic;
      passenger = Number(passengers || 0) * 225;
      driver = rate.driver;
    } else if (vehicleType === "motorcycle") {
      const rate = premiumData.motorcycle.find((r) => engineCC <= r.value);
      if (!rate || !rate.basic) return null;
      basic = rate.basic;
      passenger = rate.passenger;
      driver = rate.driver;
    } else if (vehicleType === "truck") {
      const rate = premiumData.truck.find((r) => loadTon <= r.value);
      if (!rate) return null;
      driver = 150; passenger = 150;
      if (loadTon > 5) basic = 3500 + 0.1 * 3500 * Number(extraTons || 0);
      else basic = rate.basic;
    } else if (vehicleType === "bus") {
      const rate = premiumData.bus.find((r) => Number(seats) <= r.value);
      if (!rate) return null;
      basic = rate.basic;
      passenger = Number(seats) * 225;
      driver = 150;
    } else if (vehicleType === "taxi") {
      basic = premiumData.taxi.basic;
      passenger = Number(passengers || 0) * 225;
      driver = premiumData.taxi.driver;
    }

    const totalPremium = basic + passenger + driver;
    const vat = totalPremium * 0.15;
    return { basic, passenger, driver, totalPremium, vat, final: Math.round(totalPremium + vat) };
  };

  useEffect(() => {
    const manualCheck = (formData.vehicleType === "motorcycle" || formData.vehicleType === "threeWheeler") && formData.engineCC === Infinity;
    setIsManualPremium(manualCheck);
    if (!manualCheck) {
      const breakdown = calculatePremium(formData);
      if (breakdown) setFormData((prev) => ({ ...prev, price: breakdown.final, premiumBreakdown: breakdown }));
    }
  }, [formData.vehicleType, formData.engineCC, formData.loadTon, formData.extraTons, formData.seats, formData.passengers]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!isManualPremium) onSubmit({ productType: "Motor Liability Insurance", data: formData });
  };

  const { basic, passenger, driver, totalPremium, vat, final } = formData.premiumBreakdown || {};

  return (
    <form onSubmit={handleSubmit}>
      <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 3,
                  flexWrap: "wrap",
                  gap: 2,
                }}
              >
                <Box>
                  <Typography variant="h5" sx={{ fontWeight: 600 }}>
                    Act Liability (Third Party) Insurance
                  </Typography>
              
                  <Typography
                    variant="body2"
                    sx={{ color: "text.secondary" }}
                  >
                    Please complete the form below to proceed.
                  </Typography>
                </Box>
              
                <Button
                  variant="outlined"
                  color="secondary"
                  onClick={() => setOpenDetails(true)}
                  sx={{
                    textTransform: "none",
                    borderRadius: 2,
                    px: 3,
                    fontWeight: 500,
                  }}
                >
                  View Details
                </Button> 
              </Box>

      <Grid container spacing={3}>
        {/* Step 1: Vehicle Category Selection */}
        <Grid item xs={12}>
          <Typography variant="h6" fontWeight={600} mb={2}>Select Vehicle Type</Typography>
          <Grid container spacing={2}>
            {vehicleOptions.map(({ value, label, icon }) => (
              <Grid item xs={6} sm={4} md={2} key={value}>
                <Card 
                  onClick={() => setFormData(p => ({ ...p, vehicleType: value }))}
                  sx={{ 
                    textAlign: "center", cursor: "pointer", 
                    border: formData.vehicleType === value ? "2px solid" : "1px solid", 
                    borderColor: formData.vehicleType === value ? "primary.main" : "grey.300",
                    boxShadow: formData.vehicleType === value ? 3 : 1
                  }}
                >
                  <CardContent>
                    {React.cloneElement(icon, { sx: { fontSize: 40, color: formData.vehicleType === value ? "primary.main" : "grey.500" } })}
                    <Typography variant="body2" mt={1}>{label}</Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Grid>

        {/* Dynamic Premium Inputs based on Vehicle Type */}
        {(formData.vehicleType === "motorcycle" || formData.vehicleType === "privateCar") && (
          <Grid item xs={12}>
            <Typography variant="subtitle1">Select Engine Capacity</Typography>
            <RadioGroup row name="engineCC" value={formData.engineCC.toString()} onChange={handleChange}>
              {premiumData[formData.vehicleType].map((opt) => (
                <FormControlLabel key={opt.label} value={opt.value.toString()} control={<Radio />} label={opt.label} />
              ))}
            </RadioGroup>
          </Grid>
        )}

        {formData.vehicleType === "truck" && (
          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle1">Select Load (Ton)</Typography>
            <RadioGroup row name="loadTon" value={formData.loadTon.toString()} onChange={handleChange}>
              {premiumData.truck.map((opt) => (
                <FormControlLabel key={opt.label} value={opt.value.toString()} control={<Radio />} label={opt.label} />
              ))}
            </RadioGroup>
            {formData.loadTon > 5 && (
               <TextField label="Extra Tons Above 5" name="extraTons" type="number" value={formData.extraTons} onChange={handleChange} fullWidth sx={{ mt: 2 }} />
            )}
          </Grid>
        )}

        {formData.vehicleType === "bus" && (
          <Grid item xs={12}>
            <Typography variant="subtitle1">Select Number of Seats</Typography>
            <RadioGroup row name="seats" value={formData.seats.toString()} onChange={handleChange}>
              {premiumData.bus.map((opt) => (
                <FormControlLabel key={opt.label} value={opt.value.toString()} control={<Radio />} label={opt.label} />
              ))}
            </RadioGroup>
          </Grid>
        )}

        {(formData.vehicleType === "privateCar" || formData.vehicleType === "taxi") && (
          <Grid item xs={12} sm={6} md={4}>
            <TextField label="Number of Passengers" name="passengers" type="number" value={formData.passengers} onChange={handleChange} fullWidth required />
          </Grid>
        )}

        {/* Step 2: Document Upload */}
        <Grid item xs={12}>
          <Box sx={{ p: 3, border: "2px dashed #1976d2", borderRadius: 2, textAlign: "center", bgcolor: "#f8fbff" }}>
            <Typography variant="body1" fontWeight={600} gutterBottom>Step 2: Upload Registration Document to Auto-Fill</Typography>
            <Button variant="contained" component="label" startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <CloudUploadIcon />}>
              {loading ? "Scanning..." : "Upload Blue Book"}
              <input type="file" hidden name="registrationDoc" onChange={handleChange} />
            </Button>
          </Box>
        </Grid>

        {/* Step 3: Detailed Certificate Info (Hidden until Scanned) */}
        {formData.isScanned && (
          <>
            <Grid item xs={12}><Typography variant="h6" color="primary" sx={{ borderBottom: "1px solid #eee", pb: 1 }}>Policy Certificate Details</Typography></Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField label="Owner Name" name="ownerName" value={formData.ownerName} onChange={handleChange} fullWidth required />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField label="Vehicle Registration No" name="vehicleNumber" value={formData.vehicleNumber} onChange={handleChange} fullWidth required InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField label="Chassis Number" name="chassisNumber" value={formData.chassisNumber} onChange={handleChange} fullWidth required InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField label="Engine Number" name="engineNumber" value={formData.engineNumber} onChange={handleChange} fullWidth required InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField label="Manufacturing Year" name="mfgYear" type="number" value={formData.mfgYear} onChange={handleChange} fullWidth required InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField label="Policy Start Date" name="policyStartDate" type="date" value={formData.policyStartDate} onChange={handleChange} fullWidth required />
            </Grid>
          </>
        )}

        {/* Detailed Premium Breakdown */}
        {!isManualPremium && final ? (
          <Grid item xs={12}>
            <Card variant="outlined" sx={{ p: 2, backgroundColor: "#f9f9f9", borderColor: "primary.light", boxShadow: 1 }}>
              <CardContent>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: "primary.main", textAlign: "center" }}>
                  Detailed Premium Calculation
                </Typography>
                {[
                  { label: "Basic Premium", value: basic },
                  { label: "Passenger Coverage", value: passenger },
                  { label: "Paid Driver Coverage", value: driver },
                  { label: "Total Net Premium", value: totalPremium, bold: true },
                  { label: "VAT (15%)", value: vat?.toFixed(0) },
                  { label: "Final Payable Premium", value: final, bold: true, color: "green" },
                ].map(({ label, value, bold, color }) => (
                  <Box
                    key={label}
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      borderTop: bold ? "2px solid #000" : "1px solid #ddd",
                      mt: 1,
                      pt: 1,
                    }}
                  >
                    <Typography sx={{ fontWeight: bold ? 700 : 500, color: color || "textPrimary" }}>{label}</Typography>
                    <Typography sx={{ fontWeight: bold ? 700 : 500, color: color || "textPrimary" }}>
                      {value ? `৳${value}` : "৳0"}
                    </Typography>
                  </Box>
                ))}
              </CardContent>
            </Card>
          </Grid>
        ) : isManualPremium ? (
          <Grid item xs={12}>
            <Box sx={{ p: 2, backgroundColor: "#fff3e0", border: "1px solid #ff9800", borderRadius: 1 }}>
              <Typography variant="body1" sx={{ fontWeight: 600, color: "#e65100" }}>
                For vehicles with engine capacity over 350 cc, the premium will be determined in consultation with IDRA. 
                Please submit your document to <strong>info@bgicdigital.com</strong>.
              </Typography>
            </Box>
          </Grid>
        ) : null}

        <Grid item xs={12}>
          <Button type="submit" variant="contained" fullWidth size="large" sx={{ py: 1.5, fontWeight: 700 }} disabled={isManualPremium || loading}>
            {isManualPremium ? "Contact Support" : "Add to Cart & Proceed"}
          </Button>
        </Grid>
      </Grid>

      {/* Details Modal - EXACTLY AS PER YOUR PROVIDED CODE */}
      <Dialog open={openDetails} onClose={() => setOpenDetails(false)} maxWidth="md" fullWidth>
        <DialogTitle>Motor Liability Insurance</DialogTitle>
        <DialogContent dividers>
          <Typography variant="subtitle1" gutterBottom>
            <strong>Coverage Details:</strong>
          </Typography>
          <ul>
            <li>Mandatory insurance coverage under the Motor Vehicles Ordinance, 1983.</li>
            <li>Provides protection for third-party bodily injury, death, and property damage.</li>
            <li>Covers the liability of the insured driver or owner in respect of third-party claims.</li>
            <li>Includes compensation for driver and passengers as per schedule of benefits.</li>
            <li>VAT (15%) applicable on total premium.</li>
          </ul>
          <Typography variant="subtitle1" gutterBottom>
            <strong>Exclusions:</strong>
          </Typography>
          <ul>
            <li>No coverage for damage to the insured’s own vehicle.</li>
            <li>No coverage for mechanical or electrical breakdown or wear and tear.</li>
            <li>No coverage if the vehicle is used for illegal purposes or outside permitted use.</li>
            <li>Excludes deliberate or criminal acts by the driver.</li>
            <li>Excludes use of the vehicle without valid registration or driving license.</li>
          </ul>
          <Typography variant="subtitle1" gutterBottom>
            <strong>Terms & Conditions:</strong>
          </Typography>
          <ul>
            <li>Coverage period: One year from the policy start date.</li>
            <li>Policy effective from the next working day after purchase.</li>
            <li>Premium includes all statutory levies and VAT.</li>
            <li>Claim must be reported to BGIC immediately after an accident.</li>
          </ul>
          <Typography variant="subtitle1" gutterBottom>
            <strong>Claim Process:</strong>
          </Typography>
          <ol>
            <li>Report the accident to the nearest police station and BGIC branch.</li>
            <li>Submit claim form along with supporting documents (FIR, registration, driving license).</li>
            <li>Claim investigation and assessment by authorized BGIC officer.</li>
            <li>Claim settlement as per Motor Vehicles Ordinance and IDRA guidelines.</li>
          </ol>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDetails(false)} color="primary" variant="contained">
            Got It
          </Button>
        </DialogActions>
      </Dialog>
    </form>
  );
};

export default TpForm;